import { useCallback, useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";
import { CalendarDays, Check, Loader2, MapPin, X } from "lucide-react";
import { toast } from "sonner";
import { api } from "../lib/api";
import { useAuth } from "../context/AuthContext";

const STATUS_STYLE = {
  LIVE: "text-emerald-400 border-emerald-500/40",
  PENDING_APPROVAL: "text-amber-400 border-amber-500/40",
  REJECTED: "text-red-400 border-red-500/40",
};

export default function AdminEvents() {
  const { user } = useAuth();
  const navigate = useNavigate();
  const [organisers, setOrganisers] = useState(null);
  const [events, setEvents] = useState(null);
  const [pendingCount, setPendingCount] = useState(0);
  const [working, setWorking] = useState("");

  const load = useCallback(() => {
    api.get("/admin/organisers").then((r) => setOrganisers(r.data)).catch(() => {});
    api.get("/admin/events").then((r) => { setEvents(r.data.events); setPendingCount(r.data.pending); }).catch(() => {});
  }, []);

  useEffect(() => { if (user === false) navigate("/admin/login"); }, [user, navigate]);
  useEffect(() => { if (user) load(); }, [user, load]);

  const act = async (kind, id, action) => {
    const key = `${kind}-${id}-${action}`;
    setWorking(key);
    try {
      await api.post(`/admin/${kind}/${id}/${action}`);
      toast.success(action === "approve" ? "Approved" : "Rejected");
      load();
    } catch (err) { toast.error(err.response?.data?.detail || "Action failed"); }
    finally { setWorking(""); }
  };

  if (!organisers || !events) return <div className="max-w-6xl mx-auto px-8 py-24 text-sm font-mono text-slate-500 animate-pulse" data-testid="admin-events-loading">Loading events console…</div>;

  const pendingOrgs = organisers.organisers.filter((o) => o.status === "PENDING_ADMIN_APPROVAL");
  const pendingEvents = events.filter((e) => e.status === "PENDING_APPROVAL");

  return (
    <div className="max-w-6xl mx-auto px-4 md:px-8 py-12" data-testid="admin-events-page">
      <p className="gold-kicker mb-2">Admin Console</p>
      <h1 className="font-display text-3xl font-bold text-slate-100">EVENTS & ORGANISERS</h1>
      <p className="mt-2 text-sm text-slate-400">Approve organiser accounts and review submitted events before they go live.</p>

      <div className="grid grid-cols-2 md:grid-cols-4 gap-3 mt-8 mb-10" data-testid="admin-events-stats">
        {[["Pending Organisers", pendingOrgs.length], ["Pending Events", pendingCount],
          ["Live Events", events.filter((e) => e.status === "LIVE").length], ["Total Events", events.length]].map(([l, v]) => (
          <div key={l} className="card-surface p-4 text-center" data-testid={`admin-events-stat-${l.toLowerCase().replace(/ /g, "-")}`}>
            <p className="font-mono text-2xl font-bold text-gold">{v}</p>
            <p className="text-[10px] font-mono uppercase tracking-widest text-slate-500 mt-1">{l}</p>
          </div>
        ))}
      </div>

      <div className="card-surface mb-10" data-testid="pending-organisers">
        <div className="px-5 py-4 border-b border-slate-800">
          <h2 className="font-display text-base font-bold text-slate-100">PENDING ORGANISER APPLICATIONS</h2>
        </div>
        <div className="divide-y divide-slate-800">
          {pendingOrgs.map((o) => (
            <div key={o.organiser_id} className="flex flex-wrap items-center gap-4 px-5 py-4" data-testid={`pending-organiser-${o.organiser_id}`}>
              <div className="min-w-0 flex-1">
                <p className="text-sm font-semibold text-slate-100">{o.org_name}</p>
                <p className="text-[10px] font-mono text-slate-500 mt-0.5">{o.contact_name} · {o.email} · {o.country || "—"}</p>
              </div>
              <button onClick={() => act("organisers", o.organiser_id, "approve")} disabled={working} data-testid={`approve-organiser-${o.organiser_id}`} className="btn-gold !px-4 !py-2 text-[11px]">
                {working === `organisers-${o.organiser_id}-approve` ? <Loader2 size={12} className="animate-spin" /> : <><Check size={12} /> APPROVE</>}
              </button>
              <button onClick={() => act("organisers", o.organiser_id, "reject")} disabled={working} data-testid={`reject-organiser-${o.organiser_id}`} className="btn-outline !px-4 !py-2 text-[11px] !border-red-500/40 !text-red-400">
                <X size={12} /> REJECT
              </button>
            </div>
          ))}
          {pendingOrgs.length === 0 && <p className="px-5 py-8 text-center text-sm text-slate-500">No pending organiser applications.</p>}
        </div>
      </div>

      <div className="card-surface" data-testid="pending-events">
        <div className="px-5 py-4 border-b border-slate-800">
          <h2 className="font-display text-base font-bold text-slate-100">SUBMITTED EVENTS — REVIEW QUEUE</h2>
        </div>
        <div className="divide-y divide-slate-800">
          {pendingEvents.map((e) => (
            <div key={e.id} className="flex flex-wrap items-center gap-4 px-5 py-4" data-testid={`pending-event-${e.id}`}>
              <div className="min-w-0 flex-1">
                <p className="text-sm font-semibold text-slate-100">{e.name}</p>
                <p className="text-[10px] font-mono text-slate-500 mt-0.5 flex items-center gap-2 flex-wrap">
                  <span className="flex items-center gap-1"><CalendarDays size={10} />{e.dates}</span>
                  <span className="flex items-center gap-1"><MapPin size={10} />{e.city}, {e.country}</span>
                  <span>{e.type} · {e.industry}</span>
                  {e.organiser_name && <span className="text-gold">by {e.organiser_name}</span>}
                </p>
              </div>
              <button onClick={() => act("events", e.id, "approve")} disabled={working} data-testid={`approve-event-${e.id}`} className="btn-gold !px-4 !py-2 text-[11px]">
                {working === `events-${e.id}-approve` ? <Loader2 size={12} className="animate-spin" /> : <><Check size={12} /> APPROVE & PUBLISH</>}
              </button>
              <button onClick={() => act("events", e.id, "reject")} disabled={working} data-testid={`reject-event-${e.id}`} className="btn-outline !px-4 !py-2 text-[11px] !border-red-500/40 !text-red-400">
                <X size={12} /> REJECT
              </button>
            </div>
          ))}
          {pendingEvents.length === 0 && <p className="px-5 py-8 text-center text-sm text-slate-500">No events awaiting review.</p>}
        </div>
      </div>

      <div className="card-surface mt-10" data-testid="all-events-table">
        <div className="px-5 py-4 border-b border-slate-800">
          <h2 className="font-display text-base font-bold text-slate-100">ALL EVENTS</h2>
        </div>
        <div className="divide-y divide-slate-800">
          {events.map((e) => (
            <div key={e.id} className="flex flex-wrap items-center gap-x-5 gap-y-2 px-5 py-3.5" data-testid={`admin-event-row-${e.id}`}>
              <div className="min-w-0 flex-1">
                <p className="text-sm font-semibold text-slate-100 truncate">{e.name}</p>
                <p className="text-[10px] font-mono text-slate-500 mt-0.5">{e.city}, {e.country} · {e.dates} · {e.type}</p>
              </div>
              <span className={`text-[10px] font-mono uppercase tracking-widest border px-2.5 py-1 rounded-sm ${STATUS_STYLE[e.status] || "text-slate-400 border-slate-600"}`}>{e.status.replace(/_/g, " ")}</span>
            </div>
          ))}
        </div>
      </div>
    </div>
  );
}
